import {
  ChipContainer,
  FilterBarContainer,
  FilterSearchBarContainer,
  FilterSearchBarWrapper,
  StyledChipComponent,
} from './styles';

import { CapacityPill } from './capacity-pill/capacity-pill';
import { DurationPill } from './duration-pill';
import { FavoritesFilterButton } from 'components/favorites-filter-button';
import { FilterIcon } from 'components/filter-icon/component';
import { FilterModal } from 'pages/filter-modal';
import React from 'react';
import { TouchSearchBarComponent } from 'components/touch-search-bar';
import { getResourceTypeString } from 'utils/formatResourceType';
import { useFilterBar } from './use-filter-bar.hook';
import { Route, Routes } from 'react-router-dom';
import { useFilterModalNavigation } from './use-filter-modal-navigation.hook';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useIntl } from 'react-intl';
import { FloorPill } from 'pages/resources/filter-bar/floor-pills';
import {
  CoworkerSearchButton,
  CoworkerSearchSheet,
  FloorplanFilterButton,
  FloorplanFiltersModal,
  ResourcesFilterButton,
} from 'floorplan/pages/native-floorplan/components';
import { COWORKER_SHEET_PATH } from 'floorplan/pages/native-floorplan/const';
import { useCoworkerGroups } from 'floorplan/pages/native-floorplan/hooks/use-coworker-groups.hook';
import { useCoworkerSheetNavigation } from 'floorplan/pages/native-floorplan/hooks/use-coworker-sheet-navigation.hook';
import { useCoworkerVisibility } from 'floorplan/hooks/use-coworker-visibility.hook';
import { useFloorplanViewState } from 'floorplan/pages/native-floorplan/hooks/use-floorplan-view-state.hook';
import { useListFilterCount } from './use-list-filter-count.hook';

export interface FilterBarProps {
  isVisible?: boolean;
  className?: string;
}

export const FilterBar = ({ isVisible, className }: FilterBarProps): JSX.Element => {
  const intl = useIntl();
  const { enableFavoriteResourceBookings } = useFlags();
  const {
    isCurrentConfigNexudus,
    filterByTypeEncoded,
    filterByCollectionEncoded,
    dateRangeEncoded,
    startTimeEncoded,
    endTimeEncoded,
    onCancelClick,
    chips,
    isFilteredOptionsAreVisible,
    isFavoritesOnly,
    onFavoritesOnlyClick,
  } = useFilterBar();
  const listFilterCount = useListFilterCount();
  const { onOpenModal, onCloseModal, onCTAClick } = useFilterModalNavigation();
  // The redesigned page carries the design's filter button in both of its views. Only the map filters
  // by section, so only there does the sheet behind the button change too — see
  // `FloorplanFiltersModal`; the list keeps the shared modal and its contents untouched. Every other
  // flow, the Archilogic floorplan drawer included, keeps the shared icon and modal.
  const { isFloorplanAvailable, isFloorplanView } = useFloorplanViewState();
  // Find a coworker sits next to the filters whenever the map exists and the building shows coworkers
  // at all (CV-FP-04: with the layer off there is no entry point). Opening it switches to the map.
  const { isCoworkerLayerEnabled } = useCoworkerVisibility();
  const { openSheet: openCoworkerSheet, closeSheet: closeCoworkerSheet } = useCoworkerSheetNavigation();
  // Lit while the map is focused on a group (§C3), the way the filter button is lit with filters on.
  const { activeGroup: activeCoworkerGroup } = useCoworkerGroups();

  return (
    <FilterBarContainer className={className} data-testid="filter-bar-container" isVisible={isVisible}>
      <FilterSearchBarWrapper>
        <FilterSearchBarContainer className="filter-search-bar-container">
          <TouchSearchBarComponent isResourcesPage isFloorplanUi={isFloorplanAvailable} />
          {enableFavoriteResourceBookings && (
            <FavoritesFilterButton isActive={isFavoritesOnly} onClick={onFavoritesOnlyClick} />
          )}
          {!isFloorplanAvailable && (
            <FilterIcon
              isActive={
                !!filterByTypeEncoded ||
                !!filterByCollectionEncoded ||
                !!dateRangeEncoded ||
                !!startTimeEncoded ||
                !!endTimeEncoded
              }
              onClick={onOpenModal}
            />
          )}
          {/* Prototype order: Coworkers first, then Filters at the right edge. */}
          {isFloorplanAvailable && isCoworkerLayerEnabled && (
            <CoworkerSearchButton isActive={!!activeCoworkerGroup} onClick={openCoworkerSheet} />
          )}
          {isFloorplanAvailable &&
            (isFloorplanView ? (
              <FloorplanFilterButton onClick={onOpenModal} />
            ) : (
              <ResourcesFilterButton activeFilterCount={listFilterCount} onClick={onOpenModal} />
            ))}
        </FilterSearchBarContainer>
        {/* No chip row in the redesigned page: what is applied is on the filter button as a count, and
            the design has nothing between the controls and the resources. */}
        {isFilteredOptionsAreVisible && !isFloorplanAvailable && (
          <ChipContainer>
            <DurationPill />
            <CapacityPill />
            <FloorPill />
            {chips.length > 0 &&
              chips.map(chip => (
                <StyledChipComponent
                  key={`${chip.type}-${chip.value}`}
                  label={
                    chip.type === 'type' ? getResourceTypeString(isCurrentConfigNexudus, chip.label, intl) : chip.label
                  }
                  value={chip.value}
                  isActive
                  cancelEnabled
                  onCancel={onCancelClick}
                />
              ))}
          </ChipContainer>
        )}
      </FilterSearchBarWrapper>
      <Routes>
        <Route path={COWORKER_SHEET_PATH} element={<CoworkerSearchSheet onClose={closeCoworkerSheet} />} />
        <Route
          path="filter-by"
          element={
            isFloorplanView ? (
              <FloorplanFiltersModal onCTAClick={onCTAClick} onCloseModal={onCloseModal} />
            ) : (
              <FilterModal onCTAClick={onCTAClick} onCloseModal={onCloseModal} />
            )
          }
        />
      </Routes>
    </FilterBarContainer>
  );
};
